import React, { createContext, useEffect, useState } from "react";
import cloneDeep from "lodash.clonedeep";
import * as PropTypes from "prop-types";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  getFirestore,
  increment,
} from "firebase/firestore";
import { useFirebase } from "../firebase/firebase-provider";
import { useDataStore } from "./datastore-provider";
import { useHome } from "./home-provider";
import ROUTES from "../routes/routes";
import { Meal } from "../types";

interface MealPlannerContextType {
  userMeals: Meal[] | null;
  homeMeals: Meal[] | null;
  addUserMeal: (meal: Meal) => Promise<unknown>;
  addHomeMeal: (meal: Meal) => Promise<unknown>;
  saveUserMeal: (meal: Meal) => void;
  saveHomeMeal: (meal: Meal) => void;
  removeUserMeal: (mealId: string) => void;
  removeHomeMeal: (mealId: string) => void;
  getUserMeal: (mealId: string) => Meal;
  getHomeMeal: (mealId: string) => Meal;
}

const COLLECTION_NAME = "meals";
const MealPlanner = createContext<MealPlannerContextType | undefined>(
  undefined
);

export const useMealPlanner = () => {
  const context = React.useContext(MealPlanner);
  if (context === undefined) {
    throw new Error("useMealPlanner must be used within a MealPlannerProvider");
  }
  return context;
};

MealPlannerProvider.propTypes = {
  children: PropTypes.element.isRequired,
};
export function MealPlannerProvider(props: any) {
  const [userMeals, setUserMeals] = useState(null);
  const [homeMeals, setHomeMeals] = useState(null);
  const app = useFirebase();
  const { userDb, getUserStore } = useDataStore();
  const { homeDb } = useHome();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => fetchMeals(userDb, setUserMeals), [userDb]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => fetchMeals(homeDb, setHomeMeals), [homeDb]);

  function fetchMeals(dbRef: any, setter: any) {
    if (dbRef) {
      const db = getFirestore(app);
      const mealsCollection = collection(dbRef, COLLECTION_NAME);

      const unsubscribe = onSnapshot(
        mealsCollection,
        (querySnapshot: any) => {
          setter(
            querySnapshot.docs
              .map((doc: any) => loadMeal(doc.data(), doc.id))
              .filter(filterOldMeals)
          );
        },
        (error: any) => {
          console.debug(error);
          enqueueSnackbar("Oops! Something went wrong", { variant: "error" });
        }
      );
      return unsubscribe;
    } else {
      setter(null);
    }
  }

  /**
   * Load a meal
   * Do any upgrading required between versions of the data
   * @param meal
   * @param id
   */
  function loadMeal(meal: any, id: any) {
    const upgraded = { originalServes: meal.serves, ...meal, mealId: id };
    upgraded.date = meal.date.toDate();

    if (meal.method && !Array.isArray(meal.method)) {
      upgraded.notes = upgraded.method;
      upgraded.method = [];
    }
    return upgraded;
  }

  function filterOldMeals(meal: any) {
    const minDate = new Date();
    minDate.setMonth(minDate.getMonth() - 3);
    return moment(meal.date).isSameOrAfter(minDate, "day");
  }

  function incrementRecipeUsage(
    recipeId: string,
    recipeOwnerId: string | null
  ) {
    if (recipeId) {
      // Get the database reference for the recipe owner
      // If recipeOwnerId is null, the recipe belongs to the current user
      const recipeDbRef = recipeOwnerId ? getUserStore(recipeOwnerId) : userDb;

      if (recipeDbRef) {
        const recipeDoc = doc(recipeDbRef, "recipes", recipeId);
        updateDoc(recipeDoc, {
          timesAddedAsMeal: increment(1),
        }).catch((error: any) => {
          console.debug("Failed to increment recipe usage:", error);
        });
      }
    }
  }

  function addUserMeal(meal: any) {
    return addMeal(meal, userMeals, userDb);
  }
  function addHomeMeal(meal: any) {
    return addMeal(meal, homeMeals, homeDb);
  }
  function addMeal(meal: any, theMeals: any, dbRef: any) {
    return new Promise((resolve, reject) => {
      if (dbRef) {
        const mealsCollection = collection(dbRef, COLLECTION_NAME);
        addDoc(mealsCollection, { ...meal, originalServes: meal.serves })
          .then((docRef: any) => {
            // Increment recipe usage counter if this meal is based on a recipe
            // Pass the sharedFrom field to determine which user's recipe to increment
            if (meal.recipeId) {
              incrementRecipeUsage(meal.recipeId, meal.sharedFrom || null);
            }
            resolve(docRef.id);
            enqueueSnackbar("Meal Added", {
              variant: "success",
              action: (key) => (
                <button onClick={() => navigate(ROUTES.PLANNER)}>
                  SEE PLAN
                </button>
              ),
            });
          })
          .catch((error: any) => {
            console.debug(error);
            enqueueSnackbar("Oops! Something went wrong", { variant: "error" });
            reject(error);
          });
      }
    });
  }

  function saveUserMeal(meal: any) {
    saveMeal(meal, userMeals, userDb);
  }
  function saveHomeMeal(meal: any) {
    saveMeal(meal, homeMeals, homeDb);
  }
  function saveMeal(meal: any, theMeals: any, dbRef: any) {
    if (dbRef) {
      const mealDoc = doc(dbRef, COLLECTION_NAME, meal.mealId);
      updateDoc(mealDoc, meal)
        .then(() => enqueueSnackbar("Meal Saved", { variant: "success" }))
        .catch((error: any) => {
          console.debug(error);
          enqueueSnackbar("Oops! Something went wrong", { variant: "error" });
        });
    }
  }

  /**
   * Remove a single meal
   * @param mealId The id of the meal to remove
   */
  function removeUserMeal(mealId: any) {
    removeMeal(mealId, userMeals, userDb);
  }
  function removeHomeMeal(mealId: any) {
    removeMeal(mealId, homeMeals, homeDb);
  }
  function removeMeal(mealId: any, theMeals: any, dbRef: any) {
    if (dbRef) {
      const undoRemove = () => {
        const deleted = theMeals.find((m: any) => m.mealId === mealId);
        const mealDoc = doc(dbRef, COLLECTION_NAME, mealId);
        setDoc(mealDoc, deleted).then(() =>
          enqueueSnackbar("Action Undone", { variant: "success" })
        );
      };
      const mealDoc = doc(dbRef, COLLECTION_NAME, mealId);
      deleteDoc(mealDoc)
        .then(() =>
          enqueueSnackbar("Meal Deleted", {
            variant: "success",
            action: (key) => (
              <button
                onClick={() => {
                  undoRemove();
                }}
              >
                UNDO
              </button>
            ),
          })
        )
        .catch((error: any) => {
          console.debug(error);
          enqueueSnackbar("Oops! Something went wrong", { variant: "error" });
        });
    }
  }

  function getUserMeal(mealId: any) {
    return getMeal(mealId, userMeals);
  }
  function getHomeMeal(mealId: any) {
    return getMeal(mealId, homeMeals);
  }
  function getMeal(mealId: any, theMeals: any) {
    if (!mealId || !theMeals) return null;
    const meal = theMeals.find((m: any) => m.mealId === mealId);
    return cloneDeep(meal);
  }

  return (
    <MealPlanner.Provider
      value={{
        userMeals,
        homeMeals,
        addUserMeal,
        addHomeMeal,
        saveUserMeal,
        saveHomeMeal,
        removeUserMeal,
        removeHomeMeal,
        getUserMeal,
        getHomeMeal,
      }}
    >
      {props.children}
    </MealPlanner.Provider>
  );
}
